import { useCallback, useEffect, useState, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { BrokerSplitPreview } from "@/components/BrokerSplitPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Printer, Pencil, Trash2, Briefcase, Building2, KeyRound, Copy, Check, ShieldOff, Mail, Phone, ChevronDown, ChevronRight, ArrowLeft, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import PartnerCompanyLinksPanel from "@/components/PartnerCompanyLinksPanel";

const empty = { name: "", type: "broker", color: "", agency_share_percent: "", fee_agency_share_percent: "", broker_commission_share_percent: "", broker_commission_share_split: "agency_only", broker_extra_commission_percent: "", multi_guarantee_bonus_percent: "", broker_management_fee_default: "", contact: "", contact_person: "", email: "", pec: "", phone: "", address: "", city: "", postal_code: "", province: "", rui: "", notes: "" };

export default function Brokers() {
  const { canWrite } = usePermissions();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [policies, setPolicies] = useState([]);

  const load = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([api.get("/brokers"), api.get("/policies")]);
      setItems(b.data);
      setPolicies(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        agency_share_percent: form.agency_share_percent === "" ? null : parseFloat(form.agency_share_percent),
        fee_agency_share_percent: form.fee_agency_share_percent === "" ? null : parseFloat(form.fee_agency_share_percent),
        broker_management_fee_default: form.broker_management_fee_default === "" ? null : parseFloat(form.broker_management_fee_default),
        broker_commission_share_percent: form.broker_commission_share_percent === "" ? null : parseFloat(form.broker_commission_share_percent),
        broker_commission_share_split: form.broker_commission_share_split || "agency_only",
        broker_extra_commission_percent: form.broker_extra_commission_percent === "" ? null : parseFloat(form.broker_extra_commission_percent),
        multi_guarantee_bonus_percent: form.multi_guarantee_bonus_percent === "" ? null : parseFloat(form.multi_guarantee_bonus_percent),
        email: (form.email || "").trim() || null,
        pec: (form.pec || "").trim() || null,
      };
      if (editing) await api.put(`/brokers/${editing.id}`, payload);
      else await api.post("/brokers", payload);
      toast.success("Salvato");
      setOpen(false); setForm(empty); setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare broker?")) return;
    try { await api.delete(`/brokers/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (b) => { setEditing(b); setForm({ ...empty, ...b }); setOpen(true); };

  const [portalCreds, setPortalCreds] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [audit, setAudit] = useState({ issues: [], counts: { warning: 0, info: 0, total: 0 } });
  const [auditOpen, setAuditOpen] = useState(false);

  const loadAudit = useCallback(async () => {
    try {
      const { data } = await api.get("/brokers/consistency-audit");
      setAudit(data);
    } catch (e) { /* silently ignore audit errors */ }
  }, []);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  // 🆕 Auto-espandi il broker se arriva via URL `?expand=<broker_id>`
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnToOfferId = searchParams.get("return_to_offer");
  const returnToPolicyId = searchParams.get("return_to_policy");
  const returnToWizard = searchParams.get("return_to_wizard") === "1";
  useEffect(() => {
    const expandTarget = searchParams.get("expand");
    if (expandTarget && items.some((b) => b.id === expandTarget)) {
      setExpandedId(expandTarget);
      // Scroll fluido verso la riga espansa dopo il render
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="edit-broker-${expandTarget}"]`);
        el?.closest("tr")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [searchParams, items]);

  const enablePortal = async (b) => {
    if (!window.confirm(`Abilitare l'area broker per ${b.name}? Verrà generata una password una sola volta.`)) return;
    try {
      const { data } = await api.post(`/brokers/${b.id}/enable-portal`);
      setPortalCreds({ ...data, name: b.name, recipientEmail: b.email, recipientPhone: b.phone, brokerId: b.id });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendCredsEmail = async () => {
    if (!portalCreds?.brokerId) return;
    try {
      const { data } = await api.post(`/brokers/${portalCreds.brokerId}/send-portal-credentials`, {
        portal_email: portalCreds.email, password: portalCreds.password,
        to_email: portalCreds.recipientEmail || portalCreds.email,
      });
      toast.success(`Credenziali inviate a ${data.recipient}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openCredsWhatsApp = () => {
    if (!portalCreds?.recipientPhone) { toast.warning("Broker senza numero di telefono"); return; }
    let n = String(portalCreds.recipientPhone).replace(/[^\d+]/g, "");
    if (!n.startsWith("+")) {
      if (n.startsWith("00")) n = "+" + n.slice(2);
      else if (n.startsWith("3")) n = "+39" + n;
      else n = "+" + n;
    }
    const portalUrl = `${window.location.origin}/area-riservata`;
    const msg = encodeURIComponent(
      `Ciao ${portalCreds.name || ""}, abbiamo attivato la tua area broker.\n\n🔐 Credenziali:\nEmail: ${portalCreds.email}\nPassword: ${portalCreds.password}\n\n🔗 Accedi: ${portalUrl}\n\nCambia la password al primo accesso.`,
    );
    window.open(`whatsapp://send?phone=${n.replace("+", "")}&text=${msg}`, "_blank", "noopener,noreferrer");
  };

  const disablePortal = async (b) => {
    if (!window.confirm(`Disattivare l'area broker per ${b.name}?`)) return;
    try { await api.post(`/brokers/${b.id}/disable-portal`); toast.success("Area broker disattivata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const stats = items.map((b) => {
    const own = policies.filter((p) => p.broker_id === b.id);
    // % "Provvigione agenzia" (campo fee_agency_share_percent; vuoto → stessa % dell'altro campo; nessuna → 100%)
    const provPct = b.fee_agency_share_percent ?? b.agency_share_percent ?? 100;
    return {
      ...b,
      _count: own.length,
      _premium: own.reduce((a, p) => a + (p.premium || 0), 0),
      _commission: own.reduce((a, p) => a + (p.commission_amount || 0), 0) * Number(provPct) / 100,
    };
  });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="brokers-page">
      {/* 🆕 Banner "Torna all'offerta/polizza/wizard" quando arrivi qui dal deep-link del badge broker */}
      {(returnToOfferId || returnToPolicyId || returnToWizard) && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2.5 flex items-center justify-between gap-3 print:hidden" data-testid="return-to-offer-banner">
          <p className="text-sm text-indigo-900">
            📎 Stai configurando le compagnie dal contesto di {returnToOfferId ? "un'offerta" : returnToPolicyId ? "una polizza" : "una nuova polizza (wizard)"}. Quando hai finito, torna alla modifica.
          </p>
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
            onClick={() => {
              if (returnToOfferId) navigate(`/quotes/offers?focus=${returnToOfferId}&from_brokers=1`);
              else if (returnToPolicyId) navigate(`/policies?focus=${returnToPolicyId}&from_brokers=1`);
              else if (returnToWizard) navigate(`/policies/wizard?from_brokers=1`);
            }}
            data-testid="return-to-offer-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Torna {returnToOfferId ? "all'offerta" : returnToPolicyId ? "alla polizza" : "al wizard"}
          </Button>
        </div>
      )}
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Configurazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><Briefcase className="h-8 w-8 sm:h-10 sm:w-10 text-pink-600 bg-pink-100 rounded-lg p-1.5" />Broker &amp; Agenzie</h1>
          <p className="text-slate-500 mt-1">{items.length} partner totali</p>
          {/* 🆕 Feb 2026 — Warning se ci sono partner senza email (richiesta per email conferma offerte) */}
          {items.filter((b) => !b.email).length > 0 && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800" data-testid="brokers-missing-email-warn">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span><strong>{items.filter((b) => !b.email).length}</strong> partner senza email: le email di conferma offerta non partiranno finché non la completi.</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="print-brokers" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Stampa</Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>            <DialogTrigger asChild>
              {canWrite("brokers") ? (
                <Button data-testid="add-broker-btn" className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-2" /> Nuovo partner</Button>
              ) : <span />}
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Modifica" : "Nuovo broker / agenzia"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Nome</Label>
                  <div className="flex gap-2 items-center">
                    <Input data-testid="broker-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="flex-1" />
                    <div className="flex items-center gap-1 shrink-0 border border-slate-200 rounded-md px-2 py-1 bg-slate-50" title="Colore identificativo — verrà mostrato accanto al nome del broker in tutti gli elenchi">
                      <input
                        type="color"
                        value={form.color || "#94a3b8"}
                        onChange={(e) => setForm({ ...form, color: e.target.value })}
                        className="w-6 h-6 rounded border border-slate-300 cursor-pointer p-0"
                        data-testid="broker-color-picker"
                        aria-label="Colore identificativo broker"
                      />
                      {form.color && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, color: "" })}
                          className="text-[10px] text-slate-500 hover:text-rose-600 px-1"
                          data-testid="broker-color-clear"
                          title="Rimuovi colore"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Il colore scelto sarà visibile come pallino/badge accanto al nome del broker in Polizze, Estratto Conto e altri elenchi.</p>
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="broker-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="broker">Broker</SelectItem>
                      <SelectItem value="agenzia">Agenzia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* 🆕 Sezione: Ripartizione con Broker/Agenzia partner */}
                <div className="col-span-2 mt-2 -mb-1">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600 pb-1 border-b border-slate-200">
                    🏢 Ripartizione con Broker/Agenzia partner
                  </h4>
                </div>
                <div className="col-span-2">
                  <Label>FEE che resta alla mia agenzia</Label>
                  <Input
                    data-testid="broker-agency-share"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.agency_share_percent ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const numeric = parseFloat(v);
                      const patch = { agency_share_percent: v };
                      if (!Number.isNaN(numeric) && numeric < 100 && (form.broker_commission_share_split || "agency_only") === "agency_only") {
                        patch.broker_commission_share_split = "shared";
                      }
                      setForm({ ...form, ...patch });
                    }}
                    placeholder="Es. 50"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Il resto va al broker/agenzia partner.</p>
                </div>
                <div className="col-span-2">
                  <Label>Provvigione che resta alla mia agenzia</Label>
                  <Input
                    data-testid="broker-fee-agency-share"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.fee_agency_share_percent ?? ""}
                    onChange={(e) => setForm({ ...form, fee_agency_share_percent: e.target.value })}
                    placeholder={form.agency_share_percent ? `Vuoto → usa ${form.agency_share_percent}%` : "Es. 70"}
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Vuoto → usa la stessa % della provvigione.</p>
                </div>
                <div className="col-span-2">
                  <Label>Spese di gestione fisse (100% al broker)</Label>
                  <Input
                    data-testid="broker-management-fee-default"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.broker_management_fee_default ?? ""}
                    onChange={(e) => setForm({ ...form, broker_management_fee_default: e.target.value })}
                    placeholder="Es. 25,00"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Importo fisso aggiunto al totale polizza — resta interamente al broker.</p>
                </div>
                <BrokerSplitPreview feePct={form.agency_share_percent} provPct={form.fee_agency_share_percent} mgmtFee={form.broker_management_fee_default} />

                {/* 🆕 Sezione: Retrocessioni dal broker verso la mia agenzia */}
                <div className="col-span-2 mt-3 -mb-1">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600 pb-1 border-b border-slate-200">
                    💰 Retrocessioni dal Broker → mia agenzia
                  </h4>
                </div>
                <div className="col-span-2">
                  <Label>% retrocessione base</Label>
                  <Input
                    data-testid="broker-commission-share"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.broker_commission_share_percent ?? ""}
                    onChange={(e) => setForm({ ...form, broker_commission_share_percent: e.target.value })}
                    placeholder="Es. 30"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">% della provvigione netta del broker retrocessa alla tua agenzia.</p>
                  <div className="mt-2">
                    <Label className="text-xs">Destinazione</Label>
                    <select
                      data-testid="broker-commission-share-split"
                      value={form.broker_commission_share_split || "agency_only"}
                      onChange={(e) => setForm({ ...form, broker_commission_share_split: e.target.value })}
                      className="w-full mt-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    >
                      <option value="agency_only">🏢 Solo agenzia (non spartita con collaboratori)</option>
                      <option value="shared">🤝 Ripartita con collaboratori/agenti</option>
                    </select>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>% extra su garanzie oltre RCA</Label>
                  <Input
                    data-testid="broker-extra-commission"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.broker_extra_commission_percent ?? ""}
                    onChange={(e) => setForm({ ...form, broker_extra_commission_percent: e.target.value })}
                    placeholder="Es. 10"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">% aggiuntiva applicata solo alle garanzie accessorie (Furto, Kasko, Infortuni, ecc.).</p>
                </div>
                <div className="col-span-2">
                  <Label>% Bonus Multi-Garanzia</Label>
                  <Input
                    data-testid="broker-multi-guarantee-bonus"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.multi_guarantee_bonus_percent ?? ""}
                    onChange={(e) => setForm({ ...form, multi_guarantee_bonus_percent: e.target.value })}
                    placeholder="Es. 5"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Bonus riconosciuto se la polizza contiene più di una garanzia con premio netto: % × somma dei premi netti.</p>
                </div>
                <div><Label>RUI</Label><Input data-testid="broker-rui" value={form.rui || ""} onChange={(e) => setForm({ ...form, rui: e.target.value })} /></div>
                <div><Label>Persona di riferimento</Label><Input data-testid="broker-contact-person" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input data-testid="broker-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div>
                  <Label className="flex items-center gap-1">Email <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="broker-email"
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="broker@partner.it"
                    className={!form.email ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                  />
                  {!form.email && (
                    <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Obbligatoria per ricevere le conferme di offerta al broker</p>
                  )}
                </div>
                <div><Label>PEC</Label><Input data-testid="broker-pec" type="email" value={form.pec || ""} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
                <div className="col-span-2"><Label>Indirizzo (via/piazza + numero civico)</Label><Input data-testid="broker-address" placeholder="Es. Via Roma 12/A" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Città</Label><Input data-testid="broker-city" placeholder="Es. Palermo" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>CAP</Label><Input data-testid="broker-postal-code" placeholder="90100" maxLength={5} value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} /></div>
                  <div><Label>Provincia</Label><Input data-testid="broker-province" placeholder="PA" maxLength={2} value={form.province || ""} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase().slice(0, 2) })} /></div>
                </div>
                <div className="col-span-2"><Label>Contatto vario / Note brevi</Label><Input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
                <div className="col-span-2"><Label>Note</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button data-testid="save-broker-btn" onClick={submit} disabled={!form.name} className="bg-[#0F172A] hover:bg-[#1E293B]">Salva</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Audit di consistenza rimosso: le configurazioni "100% agenzia" con quota % ≠ 100 sono OK
          — il calcolo usa la modalità split, la % è ignorata come atteso. */}

      <div className="hidden print:block mb-6">
        <h1 className="font-display text-2xl font-semibold">Broker &amp; Agenzie — Assicura</h1>
        <p className="text-xs text-slate-500">{new Date().toLocaleDateString("it-IT")}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-pink-600 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-6 py-3">Nome</th>
              <th className="text-left px-6 py-3">Tipo</th>
              <th className="text-right px-6 py-3">Ripartizione</th>
              <th className="text-right px-6 py-3">Polizze</th>
              <th className="text-right px-6 py-3">Premi</th>
              <th className="text-right px-6 py-3">Provvigioni</th>
              <th className="text-right px-6 py-3 print:hidden">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="brokers-table">
            {stats.map((b) => (
              <Fragment key={b.id}>
              <tr className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`toggle-broker-${b.id}`}
                      onClick={() => setExpandedId(expandedId === b.id ? null : b.id)}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded transition-colors ${expandedId === b.id ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"}`}
                      title={expandedId === b.id ? "Chiudi compagnie collegate" : "Mostra compagnie collegate"}
                    >
                      {expandedId === b.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      Compagnie
                    </button>
                    <Briefcase className={`h-4 w-4 ${b.type === "agenzia" ? "text-purple-500" : "text-emerald-500"}`} />
                    <span className="font-medium inline-flex items-center gap-1.5">
                      {b.color && (
                        <span
                          className="inline-block w-3 h-3 rounded-full border border-slate-200 shrink-0"
                          style={{ backgroundColor: b.color }}
                          title={`Colore identificativo: ${b.color}`}
                          data-testid={`broker-color-dot-${b.id}`}
                        />
                      )}
                      {b.name}
                    </span>
                  </div>
                  {b.contact && <div className="text-xs text-slate-500 mt-0.5 ml-6">{b.contact}</div>}
                  <div className="flex flex-col gap-0.5 mt-1 ml-6">
                    {b.email && (
                      <a
                        href={`mailto:${b.email}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`broker-email-${b.id}`}
                        className="flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 hover:underline"
                        title={`Invia email a ${b.email}`}
                      >
                        <Mail className="h-3.5 w-3.5 text-blue-600 shrink-0" />{b.email}
                      </a>
                    )}
                    {b.phone && (
                      <a
                        href={`tel:${b.phone.replace(/\s+/g, "")}`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`broker-phone-${b.id}`}
                        className="flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-900 hover:underline"
                        title={`Chiama ${b.phone}`}
                      >
                        <Phone className="h-3.5 w-3.5 text-amber-600 shrink-0" />{b.phone}
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {b.type === "agenzia" ? (
                    <span data-testid={`broker-type-${b.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                      <Building2 className="h-3 w-3" /> Agenzia
                    </span>
                  ) : (
                    <span data-testid={`broker-type-${b.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      <Briefcase className="h-3 w-3" /> Broker
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-right tabular-nums">
                  {b.agency_share_percent != null ? (
                    <div className="text-xs text-blue-700">FEE agenzia: {Number(b.agency_share_percent).toFixed(1)}%</div>
                  ) : null}
                  {b.fee_agency_share_percent != null ? (
                    <div className="text-xs text-emerald-700 font-medium">Provvigione agenzia: {Number(b.fee_agency_share_percent).toFixed(1)}%</div>
                  ) : (b.agency_share_percent != null ? (
                    <div className="text-[10px] text-slate-400 italic">Provvigione: usa {Number(b.agency_share_percent).toFixed(1)}% (legacy)</div>
                  ) : null)}
                  {b.broker_commission_share_percent != null ? (
                    <div className="text-xs text-emerald-700">
                      Retroc. provvigione: {Number(b.broker_commission_share_percent).toFixed(1)}%
                      <span className="ml-1 text-[10px] text-slate-500">
                        ({b.broker_commission_share_split === "shared" ? "🤝 ripartita" : "🏢 solo agenzia"})
                      </span>
                    </div>
                  ) : null}
                  {b.broker_extra_commission_percent != null ? (
                    <div className="text-xs text-violet-700">+ {Number(b.broker_extra_commission_percent).toFixed(1)}% su garanzie extra-RCA</div>
                  ) : null}
                  {b.broker_management_fee_default != null && Number(b.broker_management_fee_default) > 0 ? (
                    <div className="text-xs text-amber-700 font-medium">Spese gestione (100% broker): {formatCurrency(b.broker_management_fee_default)}</div>
                  ) : null}
                  {b.agency_share_percent == null && b.fee_agency_share_percent == null && (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right tabular-nums">{b._count}</td>
                <td className="px-6 py-4 text-right tabular-nums">{formatCurrency(b._premium)}</td>
                <td className="px-6 py-4 text-right tabular-nums font-medium text-emerald-700" data-testid={`broker-commission-${b.id}`}>{formatCurrency(b._commission)}</td>
                <td className="px-6 py-4 text-right print:hidden space-x-2">
                  {b.portal_enabled ? (
                    <button data-testid={`disable-broker-portal-${b.id}`} onClick={() => disablePortal(b)} className="text-amber-600 hover:text-amber-800" title="Disabilita area broker">
                      <ShieldOff className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button data-testid={`enable-broker-portal-${b.id}`} onClick={() => enablePortal(b)} className="text-emerald-600 hover:text-emerald-800" title="Abilita area broker">
                      <KeyRound className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button data-testid={`edit-broker-${b.id}`} onClick={() => openEdit(b)} className="text-blue-600 hover:text-blue-800"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`delete-broker-${b.id}`} onClick={() => remove(b.id)} className="text-red-600 hover:text-red-800"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
              {expandedId === b.id && (
                <tr className="bg-slate-50/50 print:hidden">
                  <td colSpan={7} className="px-6 py-4">
                    <PartnerCompanyLinksPanel
                      partnerType={b.type === "agenzia" ? "agenzia" : "broker"}
                      partnerId={b.id}
                      partnerName={b.name}
                      canWrite={canWrite("brokers")}
                      mode="by-partner"
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!portalCreds} onOpenChange={(o) => !o && setPortalCreds(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700"><KeyRound className="h-5 w-5" /> Area broker abilitata</DialogTitle>
          </DialogHeader>
          {portalCreds && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Credenziali generate per <strong>{portalCreds.name}</strong>. Copiale ora: la password non sarà più visibile.</p>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2 font-mono text-sm">
                <div><span className="text-slate-500 text-xs">Email:</span> {portalCreds.email}</div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-xs">Password:</span> <span className="font-bold">{portalCreds.password}</span>
                  <button
                    data-testid="copy-broker-creds"
                    onClick={async () => { await navigator.clipboard.writeText(`${portalCreds.email} / ${portalCreds.password}`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="ml-auto text-xs text-blue-600 inline-flex items-center gap-1 hover:text-blue-800"
                  >
                    {copied ? <><Check className="h-3 w-3" /> Copiato</> : <><Copy className="h-3 w-3" /> Copia</>}
                  </button>
                </div>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">⚠️ Salva queste credenziali ora. Per rigenerare la password, disabilita e riabilita l&apos;area.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button data-testid="broker-send-email" variant="outline" onClick={sendCredsEmail} disabled={!portalCreds.recipientEmail} className="border-blue-300 text-blue-700 hover:bg-blue-50">
                  <Mail className="h-4 w-4 mr-1.5" /> Invia via email
                </Button>
                <Button data-testid="broker-send-wa" variant="outline" onClick={openCredsWhatsApp} disabled={!portalCreds.recipientPhone} className="border-green-300 text-green-700 hover:bg-green-50">
                  💬 Apri WhatsApp
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPortalCreds(null)} className="bg-[#0F172A] hover:bg-[#1E293B]">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
